import { CircularProgress, IconButton } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs/ClosableDialog'
import { Button } from 'components/facets'
import { RichTextEditor, RichTextEditorHandle } from 'components/facets/RichTextEditor'
import { ContentMediaInfo, deleteContentMedia, getContentMedia, uploadContentMedia } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const serveUrl = (signedId: string) =>
  `${process.env.REACT_APP_API_ADDRESS || ''}/api/content-media/${signedId}`

interface Props {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

// A rich-text editor bundled with its own "recent media" gallery and delete dialog, backed by the
// shared content-media pool. Self-contained: each instance owns its editor ref and gallery state, so
// the picked image is always inserted into this editor.
export const RichTextEditorWithGallery = ({ value, onChange, placeholder }: Props): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const editorRef = useRef<RichTextEditorHandle>(null)

  const [galleryOpen, setGalleryOpen] = useState(false)
  const [galleryImages, setGalleryImages] = useState<ContentMediaInfo[]>([])
  const [galleryLoading, setGalleryLoading] = useState(false)
  const [imageToDelete, setImageToDelete] = useState<ContentMediaInfo | null>(null)

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const result = await uploadContentMedia(file)
    return serveUrl(result.signedId)
  }, [])

  const openGallery = async () => {
    setGalleryOpen(true)
    setGalleryLoading(true)
    try {
      setGalleryImages(await getContentMedia())
    } catch {
      setGalleryImages([])
    } finally {
      setGalleryLoading(false)
    }
  }

  const handleGalleryPick = (image: ContentMediaInfo) => {
    editorRef.current?.insertImage(serveUrl(image.signedId))
    setGalleryOpen(false)
  }

  const handleDeleteImage = async () => {
    if (!imageToDelete) return
    try {
      await deleteContentMedia(imageToDelete.id)
      setGalleryImages((prev) => prev.filter((img) => img.id !== imageToDelete.id))
      enqueueSnackbar(t('common.imageDeleted'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('common.imageDeleteFailed'), { variant: 'error' })
    } finally {
      setImageToDelete(null)
    }
  }

  return (
    <>
      <RichTextEditor
        ref={editorRef}
        value={value}
        onChange={onChange}
        onImageUpload={handleImageUpload}
        placeholder={placeholder}
      />
      <Button variant="outlined" size="small" onClick={openGallery} className="mt-2">
        <i className="fa-light fa-images me-2" />
        {t('common.recentImages')}
      </Button>

      <ClosableDialog
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <h3 className="mb-3">{t('common.recentImages')}</h3>
        {galleryLoading ? (
          <div className="d-flex justify-content-center py-4">
            <CircularProgress size={32} />
          </div>
        ) : galleryImages.length === 0 ? (
          <span className="text-muted">{t('common.noImages')}</span>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 12,
            }}
          >
            {galleryImages.map((image) => (
              <div
                key={image.id}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                  border: '1px solid var(--light-gray-border)',
                  borderRadius: 6,
                  padding: 4,
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#1976d2')}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--light-gray-border)')
                }
              >
                <IconButton
                  size="small"
                  style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    padding: 2,
                    zIndex: 1,
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setImageToDelete(image)
                  }}
                >
                  <i className="fa-light fa-xmark" style={{ fontSize: 14 }} />
                </IconButton>
                <div onClick={() => handleGalleryPick(image)}>
                  <img
                    src={serveUrl(image.signedId)}
                    alt={image.filename}
                    style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginTop: 4,
                    }}
                  >
                    {image.filename}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </ClosableDialog>

      <ClosableDialog open={!!imageToDelete} onClose={() => setImageToDelete(null)} maxWidth="xs">
        <h3 className="mb-3">{t('common.confirmDeleteImage')}</h3>
        {imageToDelete && (
          <div className="text-center mb-3">
            <img
              src={serveUrl(imageToDelete.signedId)}
              alt={imageToDelete.filename}
              style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 4 }}
            />
            <p className="small text-muted mt-2">{imageToDelete.filename}</p>
          </div>
        )}
        <div className="d-flex justify-content-evenly">
          <Button variant="outlined" onClick={() => setImageToDelete(null)}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleDeleteImage}>
            {t('common.delete')}
          </Button>
        </div>
      </ClosableDialog>
    </>
  )
}
